import { useEffect } from 'react';
import { Activity, ArrowLeft, Building2, Cpu, FileClock, Flame, LogOut, Radio, SlidersHorizontal, Stethoscope } from 'lucide-react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMarchio } from '@/app/BrandProvider';
import { useStato } from '@/app/DeviceProvider';
import { minutiRimanenti, sessioneScaduta, useSessioneTecnica } from '@/app/sessioneTecnica';
import { useAvvisi } from '@/app/useAvvisi';
import { Avvisi, Bottone, cx } from '@/ui';
import s from './shell.module.css';

export function TecnicoShell() {
  const { t } = useTranslation();
  const { marchio } = useMarchio();
  const impianto = useStato((st) => st.impianto);
  const adesso = useStato((st) => st.adesso);
  const { sessione, ultimaAttivita, esci, tocca } = useSessioneTecnica();
  const avvisi = useAvvisi((a) => a.avvisi);
  const location = useLocation();

  // RNF-25: scadenza per inattività, verificata ogni secondo dall'orologio del dispositivo.
  useEffect(() => {
    if (sessione && sessioneScaduta(ultimaAttivita, adesso)) esci('scaduta');
  }, [sessione, ultimaAttivita, adesso, esci]);

  // Ogni interazione proroga la sessione.
  useEffect(() => {
    const h = () => tocca();
    window.addEventListener('pointerdown', h);
    window.addEventListener('keydown', h);
    return () => {
      window.removeEventListener('pointerdown', h);
      window.removeEventListener('keydown', h);
    };
  }, [tocca]);

  if (!sessione) return <Navigate to="/tecnico" replace state={{ da: location.pathname }} />;

  const voci = [
    { to: '/tecnico/parco', icona: <Building2 size={22} aria-hidden />, testo: t('tecnico.nav.parco') },
    { to: '/tecnico/canali', icona: <Activity size={22} aria-hidden />, testo: t('tecnico.nav.dashboard') },
    { to: '/tecnico/io', icona: <Cpu size={22} aria-hidden />, testo: t('tecnico.nav.io') },
    { to: '/tecnico/parametri', icona: <SlidersHorizontal size={22} aria-hidden />, testo: t('tecnico.nav.parametri') },
    { to: '/tecnico/generatori', icona: <Flame size={22} aria-hidden />, testo: t('tecnico.nav.generatori') },
    { to: '/tecnico/diagnostica', icona: <Stethoscope size={22} aria-hidden />, testo: t('tecnico.nav.diagnostica') },
    { to: '/tecnico/firmware', icona: <Radio size={22} aria-hidden />, testo: t('tecnico.nav.firmware') },
    { to: '/tecnico/registro', icona: <FileClock size={22} aria-hidden />, testo: t('tecnico.nav.registro') },
  ];

  return (
    <div className={s.tecnico}>
      <header className={s.testata}>
        <div className={s.marchio}>
          <span className={s.monogramma} aria-hidden>
            {marchio.monogramma}
          </span>
          <div className={s.marchioTesto}>
            <span className={s.marchioNome}>
              {marchio.nome} · {t('tecnico.titolo')}
            </span>
            <span className={s.marchioImpianto}>{impianto ? `${impianto.nome} · ${impianto.indirizzo.citta}` : t('app.caricamento')}</span>
          </div>
        </div>
        <span className={s.spazio} />
        <div className={s.tecnicoSessione}>
          <span>{t('tecnico.accesso.sessione', { operatore: sessione.operatore, minuti: minutiRimanenti(ultimaAttivita, adesso) })}</span>
        </div>
        <NavLink to="/pannello" className={cx(s.campanello)} aria-label={t('tecnico.accesso.tornaAlPannello')}>
          <ArrowLeft size={26} aria-hidden />
        </NavLink>
        <Bottone variante="fantasma" onClick={() => esci()} aria-label={t('tecnico.accesso.esci')}>
          <LogOut size={24} aria-hidden />
          <span>{t('tecnico.accesso.esci')}</span>
        </Bottone>
      </header>
      <div className={s.tecnicoCorpo}>
        <nav className={s.tecnicoNav} aria-label={t('tecnico.nav.titolo')}>
          {voci.map((v) => (
            <NavLink key={v.to} to={v.to} className={({ isActive }) => cx(s.tecnicoVoce, isActive && s.tecnicoVoceAttiva)}>
              {v.icona}
              <span>{v.testo}</span>
            </NavLink>
          ))}
        </nav>
        <main className={s.contenuto}>
          <Outlet />
        </main>
      </div>
      <Avvisi avvisi={avvisi} />
    </div>
  );
}
